/*
 * ===============================================================================================================
 *                                Copyright 2022-2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { LockOpen } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { AuthError, InteractionStatus } from '@azure/msal-browser';
import { LuiLogoBYAnimation } from '@jda/lui-common-component-library-mui5';

import { AuthService } from '../../../auth/authService';

const LoginForm: React.FC = () => {
  const { inProgress } = useMsal();
  const [error, setError] = useState(new AuthError());

  const handleLogin = React.useCallback(async () => {
    try {
      AuthService.getInstance().clearloginCustomError();
      await AuthService.getInstance().login();
      setError(new AuthError());
    } catch (error) {
      setError(error as AuthError);
    }
  }, []);

  const getErrorTitle = (errorCode: string) => {
    switch (errorCode) {
      case 'user_cancelled':
        return 'Login cancelled by user';
      case 'popup_window_error':
        return 'You have experienced an error: ${errorCode}, please enable popup windows on your browser and try again';
      case 'not_authorized':
        return 'Please ask your administrator for permissions';
      case 'interaction_required':
        return 'You have experienced an error: ${errorCode}, please provide extra data required';
      case 'login_required':
        return 'Login required';
      case 'consent_required':
        return 'Consent required';
      case 'server_error':
        return 'An internal error has occurred. Please contact your system administrator';
      case 'token_renewal_error':
        return 'ToYou have experienced an error: ${errorCode}, click on the sign in button to try again';
      case 'endpoints_resolution_error':
        return 'You have experienced an error: ${errorCode}, please wait between 30-60 minutes and try again';
      default:
        return 'Login failed'
    }
  }

  if (inProgress === InteractionStatus.Login) {
    return (
      <Box data-testid="login-in-progress-message" display="flex" alignItems="center" flexDirection="column">
        <Box p={4} fontWeight="fontWeightLight">
          <Typography variant="body1" style={{ fontWeight: 'inherit' }} gutterBottom>
            We are signing you in...
          </Typography>
        </Box>
        <Box style={{ width: '3rem' }}>
          <LuiLogoBYAnimation fontSize="small" />
        </Box>
      </Box>
    );
  }

  const hasError = error.errorMessage || error.errorCode;
  if (hasError) {
    console.error(
      `\nPortal Login Error Code: `,
      error.errorCode,
      `\nPortal Login Error Message: `,
      error.errorMessage || error.errorCode
    );
  }

  return (
    <>
      {hasError && (
        <>
          <Box data-testid="login-error-code" alignSelf="center">
            <Typography variant="body1">
              <Box fontWeight="fontWeightBold">{error.errorCode}</Box>
            </Typography>
          </Box>
          <Box data-testid="login-error-message" paddingTop={1.5} paddingBottom={4} alignSelf="center">
            <Typography variant="body1">{getErrorTitle(error.errorCode)}</Typography>
          </Box>
        </>
      )}
      <Box alignSelf="center">
        <Button
          id="login-button"
          variant="contained"
          onClick={handleLogin}
          data-testid="login-button"
          startIcon={<LockOpen />}
          style={{ background: '#047ABC', color: 'white' }}
        >
          Sign in
        </Button>
      </Box>
    </>
  );
};

export default LoginForm;